<script setup lang="ts">
import type { OutputData } from '@editorjs/editorjs';
import ArticleParser from '~/entities/article/ui/parser/ArticleParser.vue';

const hasText = (value: string) =>
  value
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;|&#0*160;|&#x0*a0;/gi, ' ')
    .replace(/[\s\u200b\ufeff]/g, '').length > 0;

// Как и описание мероприятия, содержимое может быть JSON редактора или обычным текстом.
const parseParticipationContent = (value?: string | null) => {
  if (!value?.trim()) return null;
  try {
    const parsed = JSON.parse(value) as OutputData | null;
    if (!Array.isArray(parsed?.blocks)) return null;
    if (
      !parsed.blocks.some(
        (block) =>
          block?.type === 'paragraph' &&
          typeof block.data?.text === 'string' &&
          hasText(block.data.text),
      )
    )
      return null;
    return { blocks: parsed.blocks, text: undefined };
  } catch {
    return hasText(value) ? { blocks: undefined, text: value } : null;
  }
};

const props = defineProps<{ intro: string | null; details: string | null }>();
const introContent = computed(() => parseParticipationContent(props.intro));
const detailsContent = computed(() => parseParticipationContent(props.details));
</script>

<template>
  <section v-if="introContent || detailsContent" :class="$style.participation">
    <h2 :class="$style.title">Условия участия</h2>
    <div v-if="introContent" :class="$style.text">
      <ArticleParser v-if="introContent.blocks" :blocks="introContent.blocks" />
      <div v-else :class="$style.plain">{{ introContent.text }}</div>
    </div>
    <div v-if="detailsContent" :class="$style.details">
      <Icon
        name="my-icon:i-participation-tickets"
        :width="27"
        :height="23"
        :class="$style.icon"
      />
      <div :class="$style.text">
        <ArticleParser
          v-if="detailsContent.blocks"
          :blocks="detailsContent.blocks"
        />
        <div v-else :class="$style.plain">{{ detailsContent.text }}</div>
      </div>
    </div>
  </section>
</template>

<style module lang="scss">
.title {
  @include text_h3;
  color: var(--color-black-1);
  margin: 0 0 var(--space-3x);
}
.text {
  @include text_body1;
  min-width: 0;
  color: var(--color-black-1);
  overflow-wrap: anywhere;

  :global(p) {
    margin: 0 0 var(--space-1-5x);
    &:last-child {
      margin-bottom: 0;
    }
  }
  :global(a) {
    color: var(--color-red-1);
    text-decoration: underline;
  }
  :global(b),
  :global(strong) {
    font-weight: 600;
  }
}
.plain {
  white-space: pre-line;
}
.details {
  display: flex;
  gap: var(--space-1-5x);
  padding: var(--space-2-5x);
  border: 1px solid var(--color-gray-line);
}
.text + .details {
  margin-top: var(--space-2-5x);
}
.icon {
  flex-shrink: 0;
  color: var(--color-red-1);
}
@media (max-width: 1379px) {
  .participation {
    padding: 0 var(--space-2-5x);
  }
  .title {
    font-size: 32px;
  }
}
@media (max-width: 1023px) {
  .title {
    font-size: 24px;
  }
}
@media (max-width: 599px) {
  .text {
    font-size: 14px;
  }
  .details {
    padding: var(--space-2x);
  }
}
@media (max-width: 479px) {
  .title {
    font-size: 16px;
  }
}
</style>
